/**
 * App.tsx — АИша care-WebApp.
 *
 * После реструктуризации (Спринт 1) приложение работает только в care-режиме:
 * никаких игровых экранов (XP, квесты, ачивки, нижняя навигация).
 * Старая игровая верстка отключена и физически не загружается.
 *
 * Маршруты:
 *   /            → WelcomeScreen (точка входа для всех)
 *   /welcome     → WelcomeScreen (явный путь, на него ведёт WEB_APP_URL)
 *   /onboarding  → OnboardingScreen
 *   /subscription → SubscriptionScreen (E1, оплата)
 *   прочие care  → PlanScreen, CabinetScreen, …; несобранные — ComingSoonScreen
 *
 * См. .cursor/rules/06-design-system.mdc и 07-brand-voice.mdc.
 */

import React, { useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import api from './services/api';

import type { TelegramWebApp } from './types/telegram';

import './styles/brand-theme.css';

import ErrorBoundary from './components/ErrorBoundary';
import { reachGoal, trackPageView } from './utils/metrika';
import { captureEntryParams, isTelegramShell } from './utils/quizSession';

const WelcomeScreen     = React.lazy(() => import('./pages/care/WelcomeScreen'));
const OnboardingScreen  = React.lazy(() => import('./pages/care/OnboardingScreen'));
const BriefScreen       = React.lazy(() => import('./pages/care/BriefScreen'));
const CabinetScreen     = React.lazy(() => import('./pages/care/CabinetScreen'));
const PlanScreen        = React.lazy(() => import('./pages/care/PlanScreen'));
const RecipeScreen      = React.lazy(() => import('./pages/care/RecipeScreen'));
const FridgeScreen      = React.lazy(() => import('./pages/care/FridgeScreen'));
const TrackScreen        = React.lazy(() => import('./pages/care/TrackScreen'));
const ProgressScreen     = React.lazy(() => import('./pages/care/ProgressScreen'));
const ComingSoonScreen  = React.lazy(() => import('./pages/care/ComingSoonScreen'));
const SubscriptionScreen = React.lazy(() => import('./pages/care/SubscriptionScreen'));
const PaidScreen         = React.lazy(() => import('./pages/care/PaidScreen'));
const PdfRefScreen            = React.lazy(() => import('./pages/care/PdfRefScreen'));
const PdfDownloadScreen       = React.lazy(() => import('./pages/care/PdfDownloadScreen'));
const SpecialistRegisterScreen = React.lazy(() => import('./pages/care/SpecialistRegisterScreen'));
const WeeklyReportScreen       = React.lazy(() => import('./pages/care/WeeklyReportScreen'));
const SevenDayQuestScreen      = React.lazy(() => import('./pages/care/SevenDayQuestScreen'));
const WaterScreen              = React.lazy(() => import('./pages/care/WaterScreen'));
const SettingsScreen           = React.lazy(() => import('./pages/care/SettingsScreen'));
const EmailLoginScreen         = React.lazy(() => import('./pages/care/EmailLoginScreen'));
const AuthVerifyScreen         = React.lazy(() => import('./pages/care/AuthVerifyScreen'));
const AuthGateScreen           = React.lazy(() => import('./pages/care/AuthGateScreen'));
const QuizResultScreen         = React.lazy(() => import('./pages/care/QuizResultScreen'));

// ── Telegram Context ──────────────────────────────

interface TelegramContextType {
  tg: TelegramWebApp | null;
  user: TelegramWebApp['initDataUnsafe']['user'] | null;
  colorScheme: 'light' | 'dark';
  haptic: (type?: 'light' | 'medium' | 'heavy') => void;
}

export const TelegramContext = createContext<TelegramContextType>({
  tg: null,
  user: null,
  colorScheme: 'dark',
  haptic: () => {},
});

export const useTelegram = () => useContext(TelegramContext);

const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tg, setTg] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramWebApp['initDataUnsafe']['user'] | null>(null);
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const telegram = window.Telegram?.WebApp;

    if (telegram) {
      telegram.ready();
      telegram.expand();

      try {
        telegram.setHeaderColor('#0F0F0F');
        telegram.setBackgroundColor('#0F0F0F');
      } catch (_) {
        // Старые версии TG WebApp SDK могут не поддерживать
      }

      setTg(telegram);
      setUser(telegram.initDataUnsafe.user || null);
      setColorScheme('dark');
    }
  }, []);

  const haptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    tg?.HapticFeedback?.impactOccurred(type);
  };

  return (
    <TelegramContext.Provider value={{ tg, user, colorScheme, haptic }}>
      {children}
    </TelegramContext.Provider>
  );
};

// ── Telegram Guard ───────────────────────────────
//
// Защищённые маршруты в обычном браузере (без Telegram и без email-сессии)
// перенаправляются на /auth-gate, где показывается "Открыть в Telegram" + email.
// Публичные маршруты (welcome, pdf, specialist, auth-gate, email-login) доступны всегда.

const TELEGRAM_GUARD_FREE = new Set([
  '/', '/welcome', '/onboarding', '/brief',
  '/result',        // quiz-funnel: результат анкеты без регистрации
  '/subscription',  // у него своя обработка no-auth внутри компонента
  '/paid',          // возврат из ЮKassa: открывается и там, где входа нет
  '/email-login', '/auth-gate', '/pdf-download', '/specialist',
]);

const TelegramGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  // Проверяем именно initData, а не наличие объекта Telegram.WebApp —
  // скрипт telegram-web-app.js подключён на всех страницах и создаёт объект
  // даже в обычном браузере, но initData пустой если не внутри Mini App
  // (тот же фикс, что применили в PaywallProvider 2026-05-26).
  const isInTelegram = !!(window as any).Telegram?.WebApp?.initData;
  const hasSession = !!localStorage.getItem('aisha_session_key');

  if (!isInTelegram && !hasSession) {
    const p = location.pathname;
    const isFree =
      TELEGRAM_GUARD_FREE.has(p) ||
      p.startsWith('/pdf/') ||
      p.startsWith('/auth/');
    if (!isFree) {
      return <Navigate to="/auth-gate" replace />;
    }
  }

  return <>{children}</>;
};

// ── Paywall Guard ────────────────────────────────
//
// Когда free-пользователь просмотрел WOW-план и его демо-день закончился,
// все разделы кроме разрешённых перенаправляются на /plan (экран пейволла).

interface PaywallCtx {
  demoExpired: boolean;
  /** Подписка активна или полный доступ: экраны снимают свои inline-пейволлы (F3) */
  hasAccess: boolean;
  loading: boolean;
  refresh: () => void;
}
const PaywallContext = createContext<PaywallCtx>({ demoExpired: false, hasAccess: false, loading: true, refresh: () => {} });
export const usePaywall = () => useContext(PaywallContext);

const PaywallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [demoExpired, setDemoExpired] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  // F3: проверки идут параллельно (старт, focus, visibilitychange, refresh после оплаты) —
  // применяем только ответ последней, иначе запоздавший старый ответ «включает» пейволл обратно
  const checkSeq = useRef(0);

  const check = useCallback(async () => {
    const seq = ++checkSeq.current;
    const isLatest = () => seq === checkSeq.current;
    try {
      // Проверяем именно initData, а не наличие объекта Telegram.WebApp —
      // скрипт telegram-web-app.js подключён на всех страницах и создаёт объект
      // даже в обычном браузере, но initData пустой если не внутри Mini App.
      let hasTgAuth = !!window.Telegram?.WebApp?.initData;
      const hasEmailAuth = !!localStorage.getItem('aisha_session_key');

      // Ждём initData только в оболочке Telegram (до 8 с на медленных устройствах).
      // В браузере с email-сессией не ждём: там initData пустой всегда.
      if (!hasTgAuth && isTelegramShell()) {
        await new Promise<void>((resolve) => {
          const deadline = Date.now() + 8000;
          const tick = () => {
            if (window.Telegram?.WebApp?.initData || Date.now() >= deadline) resolve();
            else setTimeout(tick, 50);
          };
          tick();
        });
        hasTgAuth = !!window.Telegram?.WebApp?.initData;
      }

      // Браузер без сессии и без TG initData — пейволл не актуален, не ждём API.
      if (!hasTgAuth && !hasEmailAuth) {
        if (isLatest()) {
          setDemoExpired(false);
          setHasAccess(false);
        }
        return;
      }

      // initData в Telegram приходит с задержкой — цель «вход через Telegram» шлём здесь
      if (hasTgAuth) reachGoal('tg_auth', undefined, { once: true });
      // F4: оплата начата с кнопки пейволла — при возврате в приложение сверяем платёж
      // с ЮKassa сами (уведомление может опоздать). Сервер троттлит, повтор безопасен.
      let pendingPaymentId: string | null = null;
      try { pendingPaymentId = localStorage.getItem('aisha_pending_payment_id'); } catch { /* ignore */ }
      if (pendingPaymentId) {
        const c = await api.subscription.confirmPayment(pendingPaymentId);
        const st = ((c.data as any)?.data ?? c.data)?.status;
        const code = (c.data as any)?.error;
        // Брошенную/отменённую оплату больше не сверяем (ЮKassa отменяет её сама через время)
        if (st === 'canceled' || code === 'FORBIDDEN' || code === 'NOT_FOUND' || code === 'INVALID_PAYMENT_ID' || code === 'NOT_SUBSCRIPTION') {
          try { localStorage.removeItem('aisha_pending_payment_id'); } catch { /* ignore */ }
        }
      }
      const res = await api.subscription.getSubscriptionStatus();
      if (!res.success || !isLatest()) {
        // Временная ошибка API не должна включать глобальный paywall.
        return;
      }
      const data = (res.data as any)?.data ?? res.data;
      const paid = !!(data?.is_active || data?.unlimited_access);
      if (paid && pendingPaymentId) {
        try { localStorage.removeItem('aisha_pending_payment_id'); } catch { /* ignore */ }
      }
      setHasAccess(paid);
      setDemoExpired(!paid && !!(data?.demo_expired || data?.need_paywall));
    } catch {
      // Сетевая ошибка: оставляем текущее состояние.
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void check();
    // Перепроверяем статус когда пользователь возвращается из YooKassa в WebApp
    const onVisibility = () => { if (document.visibilityState === 'visible') void check(); };
    const onFocus = () => { void check(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [check]);

  // Авто-привязка email-аккаунта к TG: если в localStorage есть email session_key
  // и пользователь зашёл через Telegram — объединяем аккаунты на сервере,
  // затем удаляем ключ из localStorage (TG-сессия теперь главная).
  useEffect(() => {
    const sessionKey = localStorage.getItem('aisha_session_key');
    const hasTg = !!(window as any).Telegram?.WebApp?.initData;
    if (!hasTg || !sessionKey) return;
    api.account.autoLink(sessionKey)
      .then(res => {
        const data = (res.data as any)?.data ?? res.data;
        if (data?.linked) {
          localStorage.removeItem('aisha_session_key');
          // Перепроверяем пейволл — теперь работают лимиты объединённого аккаунта
          void check();
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PaywallContext.Provider value={{ demoExpired, hasAccess, loading, refresh: check }}>
      {children}
    </PaywallContext.Provider>
  );
};

// Маршруты доступные ВСЕГДА (даже когда демо истекло)
const PAYWALL_FREE = new Set([
  '/', '/welcome', '/onboarding', '/brief',
  '/result',         // quiz-funnel: результат анкеты (там же сохранение расчёта)
  '/cabinet',        // кабинет видно — чтобы пользователь понял что ждёт после оплаты
  '/profile',        // settings/logout must stay available even when paywall is active
  '/plan',           // сам пейволл показывается здесь
  '/subscription',   // страница оплаты
  '/paid',           // возврат после оплаты — пока вебхук не пришёл, демо ещё «истекло»
  '/email-login',
  '/specialist',
  '/pdf-download',
  '/achievements',   // ProgressScreen показывает inline paywall сам (не редирект)
  '/water',          // WaterScreen показывает inline paywall сам (не редирект)
  '/track',          // TrackScreen показывает inline paywall сам (не редирект)
  '/recipe',         // RecipeScreen показывает inline paywall сам (не редирект)
  '/fridge',         // FridgeScreen показывает inline paywall сам (не редирект)
]);

const PaywallGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { demoExpired, loading } = usePaywall();
  const location = useLocation();

  if (loading) return null;

  if (demoExpired) {
    const p = location.pathname;
    const isFree =
      PAYWALL_FREE.has(p) ||
      p.startsWith('/auth/') ||
      p.startsWith('/pdf/');
    if (!isFree) {
      return <Navigate to="/subscription" replace />;
    }
  }

  return <>{children}</>;
};

// ── BackButton handler ────────────────────────────
//
// Все care-экраны верхнего уровня — без BackButton. Если в будущем появятся
// вложенные care-экраны (например, шаги анкеты) — добавим их сюда.

const TOP_LEVEL_ROUTES = new Set<string>(['/', '/welcome', '/brief', '/result', '/cabinet', '/plan', '/pdf-download', '/subscription', '/paid', '/email-login', '/profile']);
// Публичные страницы без BackButton — проверяем через startsWith
const isTopLevelRoute = (pathname: string) =>
  TOP_LEVEL_ROUTES.has(pathname) ||
  pathname.startsWith('/pdf/') ||
  pathname.startsWith('/auth/') ||
  pathname === '/specialist';

const NavigationHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tg } = useTelegram();

  useEffect(() => {
    if (!tg) return;

    const handleBack = () => navigate(-1);

    if (isTopLevelRoute(location.pathname)) {
      tg.BackButton.hide();
    } else {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      tg.BackButton.offClick(handleBack);
    };
  }, [location.pathname, navigate, tg]);

  return <>{children}</>;
};

// ── Page Loader (care-стиль) ──────────────────────

const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0F0F0F',
    }}
  >
    <img
      src={`${process.env.PUBLIC_URL}/foto/limon-head.png`}
      alt=""
      role="presentation"
      style={{
        width: 96,
        height: 96,
        objectFit: 'contain',
        animation: 'brand-breathing 1.6s ease-in-out infinite',
      }}
    />
    <style>{`
      @keyframes brand-breathing {
        0%, 100% { transform: scale(1); opacity: 0.85; }
        50%      { transform: scale(1.05); opacity: 1; }
      }
    `}</style>
  </div>
);

// ── UTM Capture ───────────────────────────────────
// Фиксирует UTM-параметры из URL при первом открытии (первое касание).
// Сохраняет в localStorage('aisha_utm'), откуда WelcomeScreen отправит на бэкенд.
const UtmCapture: React.FC = () => {
  useEffect(() => {
    // Quiz-funnel: ?plan=pdf|sub и UTM/yclid → анонимная сессия (переживёт вход по ссылке)
    captureEntryParams();
    reachGoal('webapp_open', undefined, { once: true });
    if (window.Telegram?.WebApp?.initData) reachGoal('tg_auth', undefined, { once: true });
    try {
      // №64 фаза B: сюда перевела заглушка offline.html со второго адреса
      const fallbackFrom = new URLSearchParams(window.location.search).get('aisha_fb');
      if (fallbackFrom) {
        reachGoal('domain_fallback', {
          from: fallbackFrom,
          to: window.location.hostname,
          tg: !!window.Telegram?.WebApp?.initData,
        }, { once: true });
      }
    } catch { /* аналитика не ломает UX */ }
    try {
      if (localStorage.getItem('aisha_utm')) return; // первое касание уже записано
      const params = new URLSearchParams(window.location.search);
      const utmSource   = params.get('utm_source');
      const utmCampaign = params.get('utm_campaign');
      if (!utmSource && !utmCampaign) return; // нет UTM — не сохраняем
      localStorage.setItem('aisha_utm', JSON.stringify({
        utm_source:   utmSource,
        utm_medium:   params.get('utm_medium'),
        utm_campaign: utmCampaign,
        utm_term:     params.get('utm_term'),
        utm_content:  params.get('utm_content'),
        referrer:     document.referrer || null,
        landing_page: window.location.pathname + window.location.search,
      }));
    } catch (_) { /* localStorage недоступен */ }
  }, []);
  return null;
};

// ── Метрика: хит на каждую смену маршрута SPA ─────
const MetrikaRouteTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(window.location.pathname + window.location.search);
  }, [location.pathname]);
  return null;
};

// ── Global Unauthorized Handler ───────────────────
//
// Слушает кастомный event 'aisha:unauthorized' (от api.ts interceptor при 401)
// и StorageEvent 'aisha_logout_signal' (от SettingsScreen при ручном logout).
// Редиректит на /welcome из ЛЮБОГО экрана — не нужно добавлять listener в каждый компонент.

const UnauthorizedRedirect: React.FC = () => {
  useEffect(() => {
    const handleUnauthorized = () => {
      const { pathname } = window.location;
      // Не редиректим если уже на welcome/auth страницах
      if (
        pathname.includes('/welcome') ||
        pathname.includes('/email-login') ||
        pathname.includes('/auth') ||
        pathname.includes('/auth-gate') ||
        pathname.includes('/onboarding') ||
        pathname.includes('/brief') ||
        pathname.includes('/result') ||
        pathname.includes('/paid') ||
        pathname.includes('/subscription') // свой экран «Сначала войди» (F3)
      ) return;
      // window.location.replace работает надёжнее navigate() при async-событиях
      const pub = process.env.PUBLIC_URL || '';
      window.location.replace(`${window.location.origin}${pub}/welcome`);
    };

    const handleStorageLogout = (e: StorageEvent) => {
      if (e.key === 'aisha_logout_signal') {
        handleUnauthorized();
      }
    };

    window.addEventListener('aisha:unauthorized', handleUnauthorized);
    window.addEventListener('storage', handleStorageLogout);

    return () => {
      window.removeEventListener('aisha:unauthorized', handleUnauthorized);
      window.removeEventListener('storage', handleStorageLogout);
    };
  }, []);

  return null;
};

// ── Main App ──────────────────────────────────────

const App: React.FC = () => {
  const basename =
    process.env.REACT_APP_BASENAME ||
    (process.env.NODE_ENV === 'development' ? '/' : '/webapp');

  return (
    <ErrorBoundary>
      <TelegramProvider>
        <BrowserRouter basename={basename}>
          <UnauthorizedRedirect />
          <UtmCapture />
          <MetrikaRouteTracker />
          <PaywallProvider>
            <NavigationHandler>
              <TelegramGuard>
              <PaywallGuard>
            <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/"           element={<WelcomeScreen />} />
                <Route path="/welcome"    element={<WelcomeScreen />} />

                <Route path="/onboarding" element={<OnboardingScreen />} />
                <Route path="/brief"      element={<BriefScreen />} />
                <Route path="/result"     element={<QuizResultScreen />} />
                <Route path="/cabinet"    element={<CabinetScreen />} />
                <Route path="/plan"       element={<PlanScreen />} />

                {/* Care-флоу пока в работе — мягкая заглушка с возвратом в кабинет. */}
                <Route path="/track"        element={<TrackScreen />} />
                <Route path="/photo"        element={<TrackScreen />} />
                <Route path="/photo-analysis" element={<TrackScreen />} />
                <Route path="/water"        element={<WaterScreen />} />
                <Route path="/recipe"       element={<RecipeScreen />} />
                <Route path="/fridge"       element={<FridgeScreen />} />
                <Route path="/achievements" element={<ProgressScreen />} />
                <Route path="/profile"      element={<SettingsScreen />} />
                <Route path="/subscription" element={<SubscriptionScreen />} />
                <Route path="/paid"         element={<PaidScreen />} />
                <Route path="/help"         element={<ComingSoonScreen reason="help" />} />
                {/* Чат с АИшей удалён по решению владельца (см. PROJECT_JOURNAL спринт 2). */}
                <Route path="/chat"         element={<Navigate to="/cabinet" replace />} />

                {/* Блок F: PDF по реф-ссылке специалиста (публичный маршрут) */}
                <Route path="/pdf/:refCode"  element={<PdfRefScreen />} />
                <Route path="/pdf-download"  element={<PdfDownloadScreen />} />

                {/* Блок F: Регистрация специалиста-партнёра (публичный маршрут) */}
                <Route path="/specialist"    element={<SpecialistRegisterScreen />} />

                {/* Итог недели */}
                <Route path="/weekly-report" element={<WeeklyReportScreen />} />

                {/* Квест: 7 дней по плану */}
                <Route path="/quest" element={<SevenDayQuestScreen />} />

                {/* Email-авторизация и quiz-funnel */}
                <Route path="/email-login"    element={<EmailLoginScreen />} />
                <Route path="/auth/:token"    element={<AuthVerifyScreen />} />
                <Route path="/auth-gate"      element={<AuthGateScreen />} />

                {/* Любой неизвестный путь → на welcome */}
                <Route path="*" element={<Navigate to="/welcome" replace />} />
              </Routes>
            </React.Suspense>
              </PaywallGuard>
              </TelegramGuard>
            </NavigationHandler>
          </PaywallProvider>
        </BrowserRouter>
      </TelegramProvider>
    </ErrorBoundary>
  );
};

export default App;
